import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import './editors.css';

interface PersonEditorProps {
  value: string[];
  options: string[];
  currentUserName?: string;
  onChange: (values: string[]) => void;
  onClose: () => void;
  multiple?: boolean;
}

// Generate consistent color for avatar based on name
const getAvatarColor = (name: string): string => {
  const colors = [
    '#db2777', // pink
    '#22c55e', // green
    '#3b82f6', // blue
    '#f97316', // orange
    '#a855f7', // purple
    '#06b6d4', // cyan
    '#ef4444', // red
    '#eab308', // yellow
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Get initials from name
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const PersonEditor: React.FC<PersonEditorProps> = ({
  value,
  options,
  currentUserName,
  onChange,
  onClose,
  multiple = true,
}) => {
  const [searchText, setSearchText] = useState('');
  const [localValues, setLocalValues] = useState<string[]>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onChange(localValues);
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [localValues, onChange, onClose]);

  // Filter options based on search
  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleRemoveValue = (val: string) => {
    const newValues = localValues.filter(v => v !== val);
    setLocalValues(newValues);
  };

  const handleToggleValue = (val: string) => {
    if (localValues.includes(val)) {
      handleRemoveValue(val);
    } else {
      if (multiple) {
        setLocalValues([...localValues, val]);
      } else {
        setLocalValues([val]);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onChange(localValues);
      onClose();
    } else if (e.key === 'Backspace' && !searchText && localValues.length > 0) {
      handleRemoveValue(localValues[localValues.length - 1]);
    }
  };

  const isCurrentUser = (name: string): boolean => {
    if (!currentUserName) return false;
    return name.toLowerCase() === currentUserName.toLowerCase();
  };

  return (
    <div
      ref={containerRef}
      className="person-editor"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Selected people */}
      <div className="person-editor-selected">
        {localValues.map((person) => {
          const color = getAvatarColor(person);
          return (
            <div key={person} className="person-editor-selected-item">
              <div
                className="person-editor-avatar"
                style={{ backgroundColor: color }}
              >
                {getInitials(person)}
              </div>
              <span className="person-editor-name">{person}</span>
              <button
                className="person-editor-remove"
                onClick={() => handleRemoveValue(person)}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="person-editor-input"
          placeholder={localValues.length === 0 ? "Search people..." : ""}
        />
      </div>

      {/* Hint text */}
      <div className="person-editor-hint">
        Select as many as you like
      </div>

      {/* Options list */}
      <div className="person-editor-options">
        {filteredOptions.map((person) => {
          const color = getAvatarColor(person);
          const isSelected = localValues.includes(person);

          return (
            <div
              key={person}
              className={`person-editor-option ${isSelected ? 'selected' : ''}`}
              onClick={() => handleToggleValue(person)}
            >
              <div
                className="person-editor-avatar"
                style={{ backgroundColor: color }}
              >
                {getInitials(person)}
              </div>
              <span className="person-editor-option-name">
                {person}
                {isCurrentUser(person) && (
                  <span className="person-editor-you">(You)</span>
                )}
              </span>
            </div>
          );
        })}

        {filteredOptions.length === 0 && (
          <div className="person-editor-empty">
            No people found
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonEditor;
